/**
 * Pantalla de Cocina (Kitchen Display System - KDS).
 *
 * Diseñada para tablets Android de 10" operadas con las manos ocupadas.
 * Muestra comandas en tiempo real, cronómetros por orden con cambio de color,
 * aviso sonoro al entrar un pedido y el botón de "Agotado" en caliente.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { cantidad } from '../lib/formato';
import { reproducirTonoOrdenNueva } from '../lib/audio';
import { solicitarWakeLock, liberarWakeLock } from '../lib/wakelock';
import { alRecibir, alCambiarConexion, type EstadoConexion } from '../lib/socket';

interface Modificador {
  nombre_opcion: string;
  precio_extra: number | string;
}

interface DetalleOrdenCocina {
  id: number;
  producto_id: number;
  producto_nombre: string;
  cantidad: number | string;
  minutos_prep: number | null;
  notas: string | null;
  estado: string;
  modificadores: Modificador[];
}

interface OrdenCocina {
  id: number;
  numero_orden: number;
  estado: 'recibida' | 'en_preparacion' | 'lista' | 'entregada' | 'cancelada';
  total: number | string;
  notas: string | null;
  tipo: 'mesa' | 'domicilio' | 'mostrador';
  nombre_cliente: string | null;
  creado_en: string;
  actualizado_en: string;
  mesa_numero: number | null;
  mesa_nombre: string | null;
  meta_minutos: number;
  detalles: DetalleOrdenCocina[];
}

interface ProductoAgotado {
  id: number;
  nombre: string;
  estado: 'publicado' | 'agotado' | 'borrador' | 'archivado';
}

export function Cocina({ sucursalId }: { sucursalId: number }) {
  const [ordenes, setOrdenes] = useState<OrdenCocina[]>([]);
  const [cargando, setCargando] = useState(true);
  const [estadoRed, setEstadoRed] = useState<EstadoConexion>('conectado');
  const [ahora, setAhora] = useState(Date.now());
  const [sonidoHabilitado, setSonidoHabilitado] = useState(true);

  // Modal de Agotados
  const [mostrarModalAgotados, setMostrarModalAgotados] = useState(false);
  const [productos, setProductos] = useState<ProductoAgotado[]>([]);
  const [cargandoProds, setCargandoProds] = useState(false);

  // Reloj vivo y contador de segundos
  useEffect(() => {
    const timer = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Wake Lock de pantalla activa
  useEffect(() => {
    solicitarWakeLock();
    return () => liberarWakeLock();
  }, []);

  // Suscripción a estado de red
  useEffect(() => alCambiarConexion(setEstadoRed), []);

  const cargarOrdenes = useCallback(async () => {
    try {
      const datos = await api<OrdenCocina[]>(`/mesas/cocina/activas?sucursalId=${sucursalId}`);
      setOrdenes(datos);
    } catch (err) {
      console.error('[cocina] Error al cargar órdenes:', err);
    } finally {
      setCargando(false);
    }
  }, [sucursalId]);

  useEffect(() => {
    cargarOrdenes();

    const unsubNueva = alRecibir<OrdenCocina>('orden:nueva', () => {
      if (sonidoHabilitado) reproducirTonoOrdenNueva();
      cargarOrdenes();
    });

    const unsubCambio = alRecibir('orden:cambio', () => {
      cargarOrdenes();
    });

    const unsubSinc = alRecibir('sincronizar', () => {
      cargarOrdenes();
    });

    return () => {
      unsubNueva();
      unsubCambio();
      unsubSinc();
    };
  }, [cargarOrdenes, sonidoHabilitado]);

  async function cambiarEstado(ordenId: number, nuevoEstado: string) {
    try {
      await api(`/mesas/ordenes/${ordenId}/estado?sucursalId=${sucursalId}`, {
        method: 'PATCH',
        body: { estado: nuevoEstado },
      });
      await cargarOrdenes();
    } catch (err: any) {
      alert(err?.message || 'No se pudo actualizar el estado de la comanda.');
    }
  }

  async function abrirModalAgotados() {
    setMostrarModalAgotados(true);
    try {
      setCargandoProds(true);
      const res = await api<ProductoAgotado[]>(`/catalogo/productos?sucursalId=${sucursalId}&limite=500`);
      setProductos(res.filter((p) => p.estado === 'publicado' || p.estado === 'agotado'));
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoProds(false);
    }
  }

  async function toggleAgotado(producto: ProductoAgotado) {
    const nuevoEstado = producto.estado === 'publicado' ? 'agotado' : 'publicado';
    try {
      await api(`/catalogo/productos/${producto.id}/estado?sucursalId=${sucursalId}`, {
        method: 'PATCH',
        body: { estado: nuevoEstado },
      });
      setProductos((prev) =>
        prev.map((p) => (p.id === producto.id ? { ...p, estado: nuevoEstado } : p))
      );
    } catch (err: any) {
      alert(err?.message || 'Error al cambiar estado del producto.');
    }
  }

  // Formato de reloj digital HH:MM:SS
  const relojDigital = useMemo(() => {
    const d = new Date(ahora);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, [ahora]);

  return (
    <div className="min-h-screen bg-noche-fondo text-cal p-4 font-cuerpo">
      {/* Cabecera KDS */}
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-salvia/20 bg-noche-superficie p-4 shadow-md">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-nopal/20 text-2xl">
            🍳
          </div>
          <div>
            <h1 className="font-titulo text-2xl font-extrabold tracking-tight text-white">
              Pantalla de Cocina
            </h1>
            <div className="flex items-center gap-2 text-xs text-salvia">
              <span>{relojDigital}</span>
              <span>·</span>
              <span className="font-bold text-cal">{ordenes.length} comandas activas</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Sonido toggle */}
          <button
            onClick={() => setSonidoHabilitado(!sonidoHabilitado)}
            className={`rounded-xl px-3 py-2 text-xs font-bold transition border ${
              sonidoHabilitado
                ? 'border-nopal/40 bg-nopal/20 text-nopal-claro'
                : 'border-salvia/30 bg-cal/10 text-salvia'
            }`}
          >
            {sonidoHabilitado ? '🔊 Sonido Activo' : '🔇 Silenciado'}
          </button>

          {/* Botón de Agotados */}
          <button
            onClick={abrirModalAgotados}
            className="rounded-xl border border-miel/40 bg-miel/20 px-3.5 py-2 text-xs font-bold text-miel-claro hover:bg-miel/30 transition"
          >
            ⚠️ Marcar Agotado
          </button>

          {/* Indicador de red */}
          <div
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              estadoRed === 'conectado' ? 'bg-nopal/20 text-nopal-claro' : 'bg-barro/30 text-barro'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                estadoRed === 'conectado' ? 'bg-nopal' : 'bg-barro animate-ping'
              }`}
            />
            <span>{estadoRed === 'conectado' ? 'WiFi OK' : 'Sin Red'}</span>
          </div>
        </div>
      </header>

      {/* Grid de Comandas */}
      {cargando ? (
        <div className="py-24 text-center text-salvia font-bold text-lg">
          Cargando comanda de cocina…
        </div>
      ) : ordenes.length === 0 ? (
        <div className="my-16 text-center text-salvia">
          <div className="text-5xl">✨</div>
          <h2 className="mt-3 font-titulo text-2xl font-bold text-white">Cocina al día</h2>
          <p className="mt-1 text-sm">No hay órdenes pendientes en este momento.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {ordenes.map((ord) => {
            const lista = ord.estado === 'lista';
            const fechaCreacion = new Date(ord.creado_en).getTime();
            // Una vez lista, el reloj se congela en el momento en que cocina
            // la marcó: seguir corriendo después no mide nada de cocina, sólo
            // cuánto tarda el cliente en pasar por ella.
            const fechaFin = lista ? new Date(ord.actualizado_en).getTime() : ahora;
            const segundosTranscurridos = Math.max(0, Math.floor((fechaFin - fechaCreacion) / 1000));
            const minutosTranscurridos = Math.floor(segundosTranscurridos / 60);
            const segRestantes = segundosTranscurridos % 60;

            const metaMinutos = ord.meta_minutos || 12;
            const porcentaje = (minutosTranscurridos / metaMinutos) * 100;

            let colorStatusClass = 'border-nopal bg-noche-superficie';
            let badgeColorClass = 'bg-nopal text-white';
            let urgente = false;

            if (lista) {
              // Ya se hizo su trabajo: aunque haya tardado, no tiene caso
              // seguir marcándola en rojo como si cocina siguiera atrasada.
              colorStatusClass = 'border-nopal bg-noche-superficie';
              badgeColorClass = 'bg-nopal text-white';
            } else if (porcentaje >= 100) {
              colorStatusClass = 'border-barro bg-barro-oscuro/20 shadow-barro/20 shadow-lg';
              badgeColorClass = 'bg-barro text-white animate-pulse';
              urgente = true;
            } else if (porcentaje >= 50) {
              colorStatusClass = 'border-miel bg-noche-superficie';
              badgeColorClass = 'bg-miel text-white';
            }

            return (
              <div
                key={ord.id}
                className={`flex flex-col justify-between rounded-2xl border-2 p-4 shadow-lg transition-all ${colorStatusClass}`}
              >
                <div>
                  {/* Encabezado de la comanda */}
                  <div className="flex items-center justify-between border-b border-salvia/20 pb-3">
                    <div>
                      <span className="font-titulo text-2xl font-extrabold text-white">
                        #{ord.numero_orden}
                      </span>
                      <p className="text-sm font-bold text-nopal-claro">
                        {ord.tipo === 'mostrador'
                          ? `🛍️ Para llevar — ${ord.nombre_cliente || 'sin nombre'}`
                          : ord.mesa_nombre || `Mesa ${ord.mesa_numero || 'S/N'}`}
                      </p>
                    </div>

                    <div className="text-right">
                      <div className={`rounded-xl px-2.5 py-1 font-mono text-sm font-bold ${badgeColorClass}`}>
                        ⏱ {minutosTranscurridos}:{segRestantes < 10 ? '0' : ''}{segRestantes}
                      </div>
                      {urgente && (
                        <span className="text-[10px] font-extrabold tracking-wider uppercase text-barro">
                          ¡Retrasada!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Notas globales de la orden */}
                  {ord.notas && (
                    <div className="my-2.5 rounded-xl border border-miel/30 bg-miel/10 p-2.5 text-xs text-miel-claro">
                      <span className="font-bold">Nota de mesa:</span> "{ord.notas}"
                    </div>
                  )}

                  {/* Lista de platillos */}
                  <div className="my-3 space-y-2.5">
                    {ord.detalles.map((det) => (
                      <div
                        key={det.id}
                        className="rounded-xl border border-salvia/15 bg-cal/5 p-2.5"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-nopal/30 font-titulo text-base font-extrabold text-white">
                              {cantidad(det.cantidad)}
                            </span>
                            <span className="font-titulo text-base font-bold text-white">
                              {det.producto_nombre}
                            </span>
                          </div>
                        </div>

                        {/* Modificadores */}
                        {det.modificadores && det.modificadores.length > 0 && (
                          <div className="mt-1.5 pl-9 space-y-0.5">
                            {det.modificadores.map((m, idx) => (
                              <p key={idx} className="text-xs font-semibold text-miel-claro">
                                • {m.nombre_opcion}
                              </p>
                            ))}
                          </div>
                        )}

                        {/* Notas individuales del platillo */}
                        {det.notas && (
                          <p className="mt-1 pl-9 text-xs font-semibold italic text-barro-claro">
                            👉 "{det.notas}"
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Botones de acción táctiles grandes */}
                <div className="mt-4 border-t border-salvia/20 pt-3">
                  {ord.estado === 'recibida' ? (
                    <button
                      onClick={() => cambiarEstado(ord.id, 'en_preparacion')}
                      className="w-full rounded-xl bg-miel py-3.5 font-titulo text-lg font-extrabold text-white transition active:scale-95 hover:bg-miel-oscuro shadow-md"
                    >
                      ▶ Preparar
                    </button>
                  ) : ord.estado === 'en_preparacion' ? (
                    <button
                      onClick={() => cambiarEstado(ord.id, 'lista')}
                      className="w-full rounded-xl bg-nopal py-3.5 font-titulo text-lg font-extrabold text-white transition active:scale-95 hover:bg-nopal-oscuro shadow-md animate-pulse"
                    >
                      ✓ Marcar Listo
                    </button>
                  ) : (
                    <div className="rounded-xl border border-nopal/40 bg-nopal/20 py-2.5 text-center font-titulo text-sm font-bold text-nopal-claro">
                      {ord.tipo === 'mostrador' ? '✓ Listo — Avisado al cliente' : '✓ Listo — Avisado al Mesero'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal "Se acabó un platillo" */}
      {mostrarModalAgotados && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/80 p-4 backdrop-blur-md">
          <div className="w-full max-w-lg rounded-3xl border border-salvia/20 bg-noche-superficie p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-salvia/20 pb-3">
              <div>
                <h3 className="font-titulo text-xl font-bold text-white">Gestionar Agotados</h3>
                <p className="text-xs text-salvia">Oculta platillos del menú público en tiempo real</p>
              </div>
              <button
                onClick={() => setMostrarModalAgotados(false)}
                className="rounded-full p-2 text-salvia hover:bg-cal/10"
              >
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[60vh] overflow-y-auto space-y-2 pr-1">
              {cargandoProds ? (
                <p className="py-8 text-center text-salvia">Cargando productos…</p>
              ) : (
                productos.map((prod) => (
                  <div
                    key={prod.id}
                    className="flex items-center justify-between rounded-xl border border-salvia/15 bg-cal/5 p-3"
                  >
                    <span className="font-titulo text-sm font-bold text-white">{prod.nombre}</span>
                    <button
                      onClick={() => toggleAgotado(prod)}
                      className={`rounded-xl px-4 py-1.5 text-xs font-extrabold transition ${
                        prod.estado === 'publicado'
                          ? 'bg-nopal text-white hover:bg-nopal-oscuro'
                          : 'bg-barro text-white hover:bg-barro-oscuro'
                      }`}
                    >
                      {prod.estado === 'publicado' ? 'Disponible' : 'AGOTADO'}
                    </button>
                  </div>
                ))
              )}
            </div>

            <button
              onClick={() => setMostrarModalAgotados(false)}
              className="w-full rounded-xl bg-salvia/20 py-3 font-bold text-white hover:bg-salvia/30"
            >
              Listo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
